import {
  type DtoEthnicity,
  type DtoGetDemographicsResp,
  type DtoRace,
  type DtoUpdateDemographicsReq,
  type DomainBiologicalSexType,
} from "@porchpass/api-client";
import { filterEntriesRequiringOther } from "@porchpass/ui";
import {
  DLA_ETHNICITY_TYPES_REQUIRING_OTHER,
  DLA_RACE_TYPES_REQUIRING_OTHER,
} from "src/forms/validation/conditionalOtherConfigs";
import { requiredField } from "./required";

function filterEthnicitiesForApi(
  ethnicities?: DtoEthnicity[],
): DtoEthnicity[] | undefined {
  if (!ethnicities?.length) return ethnicities;
  const entries = ethnicities.map((e) => ({
    type: e.ethnicity_type ?? "",
    other: e.ethnicity_other,
  }));
  const filtered = filterEntriesRequiringOther(
    entries,
    DLA_ETHNICITY_TYPES_REQUIRING_OTHER,
  );
  return filtered.map((e) => ({
    ethnicity_type: e.type as DtoEthnicity["ethnicity_type"],
    ethnicity_other: e.other,
  }));
}

function filterRacesForApi(races?: DtoRace[]): DtoRace[] | undefined {
  if (!races?.length) return races;
  const entries = races.map((r) => ({
    type: r.race_type ?? "",
    other: r.race_other,
  }));
  const filtered = filterEntriesRequiringOther(
    entries,
    DLA_RACE_TYPES_REQUIRING_OTHER,
  );
  return filtered.map((r) => ({
    race_type: r.type as DtoRace["race_type"],
    race_other: r.other,
  }));
}

export type EthnicityFormData = {
  ethnicities?: DtoEthnicity[];
};

export type RaceFormData = {
  races?: DtoRace[];
};

export type SexFormData = {
  biological_sexes?: DomainBiologicalSexType[];
};

export function fromEthnicities(
  data: DtoGetDemographicsResp,
): EthnicityFormData {
  return {
    ethnicities: data.ethnicities,
  };
}

export function toEthnicitiesUpdateReq(
  data: EthnicityFormData,
): DtoUpdateDemographicsReq {
  return {
    ethnicities: filterEthnicitiesForApi(data.ethnicities),
  };
}

export function fromRaces(data: DtoGetDemographicsResp): RaceFormData {
  return {
    races: data.races,
  };
}

export function toRacesUpdateReq(data: RaceFormData): DtoUpdateDemographicsReq {
  return {
    races: filterRacesForApi(data.races),
  };
}

export function fromBiologicalSexes(data: DtoGetDemographicsResp): SexFormData {
  return {
    biological_sexes:
      data.biological_sexes?.map((obj) =>
        requiredField(obj.biological_sex_type, "biological_sex_type"),
      ) ?? [],
  };
}

export function toBiologicalSexesUpdateReq(
  data: SexFormData,
): DtoUpdateDemographicsReq {
  return {
    biological_sexes: (data.biological_sexes ?? []).map((val) => ({
      biological_sex_type: val,
    })),
  };
}
